import {
  admisionService,
  formCompValueService,
  odontogramaService,
  odontologicaService
} from '@/services'
import { generateDocMedico, buildDocMedicoDefinition } from '@/modules/Core/usings/impresion'
import { tipoImpresionAsistencialDefault } from '@/modules/Core/usings/constantes'
import type { TDocumentDefinitions } from 'pdfmake/interfaces'
import type {
    IFormComponent,
  IFormStructure,
  IFormxtab
} from '@/modules/Configuracion/interfaces/IFormComponent'
import { useGraficas } from '@/modules/Asistencial/composables/useGraficas'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'
import type { ICitaTechnologiasSalud, IGraficaItem } from '@/modules/Asistencial/interfaces'
import { mapTabForm, type IGeneratePdfOptions, type IProcessedData } from '../usings/generales'
import { useOdontograma } from '@/modules/Asistencial/composables/useOdontograma'
import { useDocumentoAtencionPdf } from '@/modules/Asistencial/composables/useDocumentoAtencionPdf'

export const usePdf = () => {
    const { generarDocumentoAtencion } = useDocumentoAtencionPdf()
    const { generarSVG } = useOdontograma('impresion')
    /**
     * Obtiene gráficas usando useGraficas
     */
    const obtenerGraficas = async (
      admision: ICitaAdmision,
      technologia: ICitaTechnologiasSalud
    ): Promise<IGraficaItem[]> => {
      if (!admision.paciente) return []

      const { getGraficasBase64 } = useGraficas(
        admision.paciente,
        [technologia]
      )
      try {
        const { data: graficasData } = await getGraficasBase64()
        return graficasData || []
      } catch (graficasError) {
        console.warn('Error al obtener gráficas:', graficasError)
        return []
      }
    }

    /**
     * Obtiene odontogramas del DOM
     */
    const obtenerOdontogramas = async (cittId: number): Promise<{ titulo: string, svg: string }[]> => {
      if (typeof document === 'undefined') return []

      try {
        const titulos = [] as { titulo: string, svg: string}[]
        const { data:odontogramasData } = await odontogramaService.list()
        const odontogramas = odontogramasData.body

        for (const odontograma of odontogramas) {
          const { data } = await odontologicaService.listOdontologicas(cittId, odontograma.coodId as number, false)
          const xml = await generarSVG(odontograma, cittId, true, { isView: true, data: data.body })
          titulos.push({ titulo: odontograma.coodNombre as string, svg: xml })
        }

        return titulos
      } catch (odontogramaError) {
        console.warn('Error al obtener odontogramas:', odontogramaError)
        return []
      }
    }

    /**
     * Procesa los datos base para generar el FormStructure
     */
    const procesarDatosBase = async (id: number): Promise<{
      components: IFormComponent[]
      technologia: ICitaTechnologiasSalud
      admision: ICitaAdmision
    }> => {
      const { data } = await admisionService.getAdmisionByTechId(id, { withAssociationOrders: true, withPacienteSnapshot: true })
      const datosAdmision = data.body
      const admision = datosAdmision

      if (!admision.technologias || admision.technologias.length === 0) {
        throw new Error('No se encontraron tecnologías para la admisión')
      }

      const technologia = admision.technologias.find(item => item.cittId == id)
      if (!technologia) {
        throw new Error('No se encontró la tecnología especificada')
      }
      const { data: dataGuardado } = await formCompValueService.document(id, { isForm: true, onlyTabs: true })
      const formulario = dataGuardado.body

      if (!formulario || !formulario.tabs) {
        throw new Error('No se encontró el formulario guardado para esta tecnología')
      }
      const tabsWithComponentsForm = formulario.tabs?.map(mapTabForm) as IFormxtab[]
      const components = tabsWithComponentsForm.map(comp => comp.components).flat().map(item => {
        return {
          ...item,
          codigo: technologia.cittCodTechnologia,
          tecnologia: technologia.cittTechnologia,
        }
      }) as IFormComponent[]
      return {
        components,
        technologia,
        admision
      }
    }

    /**
     * Genera un PDF para una tecnología de salud específica
     * Puede trabajar tanto con ID (obteniendo datos) como con datos ya procesados
     */
    const generatePdf = async (
      idOrData: number | IProcessedData,
      options: IGeneratePdfOptions
    ): Promise<Blob | void> => {
      try {
        let baseData: {
          components: IFormComponent[]
          technologia: ICitaTechnologiasSalud
          admision: ICitaAdmision
        }
        let graficas: IGraficaItem[] = []
        let odontogramas: { titulo: string, svg: string }[] = []

        // Obtener o usar los datos base
        if (typeof idOrData === 'number') {
          baseData = await procesarDatosBase(idOrData)
        } else {
          baseData = {
            components: idOrData.components,
            technologia: idOrData.technologia,
            admision: idOrData.admision
          }
          // Si ya tenemos gráficas y odontogramas, usarlos
          graficas = idOrData.graficas || []
          odontogramas = idOrData.odontogramas || []
        }
        const generadoDocumento = await generarDocumentoAtencion(baseData.technologia, options.tipoImpresion)
        if(generadoDocumento) return

        // Obtener gráficas si están habilitadas y no las tenemos ya
        if (options.includeGraficas !== false && graficas.length === 0) {
          graficas = await obtenerGraficas(baseData.admision, baseData.technologia)
        }

        // Obtener odontogramas si están habilitados y no los tenemos ya
        if (options.includeOdontogramas !== false && odontogramas.length === 0) {
          odontogramas = await obtenerOdontogramas(baseData.technologia.cittId as number)
        }

        // Crear el contenido final del formulario
        const formContent: IFormStructure = {
          components: baseData.components,
          technologia: baseData.technologia,
          admision: baseData.admision,
          odontogramas: odontogramas,
          graficas: graficas
        }

        // Generar el PDF
        return await generateDocMedico(
          formContent,
          options.tipoImpresion,
          options.isPreview,
          options.returnAsBlob,
          options.includeOdontogramas
        )

      } catch (error) {
        console.error('Error al generar PDF:', error)
        throw error
      }
    }

    /**
     * Construye la definición pdfmake de la historia (HISTORIA) de una atención,
     * sin crear/abrir el PDF. Útil para concatenar varias atenciones en un solo PDF.
     */
    const buildHistoriaDefinition = async (
      cittId: number,
      fechaStr?: string,
      tecnologiaNombre?: string
    ): Promise<TDocumentDefinitions | null> => {
      const baseData = await procesarDatosBase(cittId)
      const graficas = await obtenerGraficas(baseData.admision, baseData.technologia)
      const odontogramas = await obtenerOdontogramas(baseData.technologia.cittId as number)
      const formContent: IFormStructure = {
        components: baseData.components,
        technologia: baseData.technologia,
        admision: baseData.admision,
        graficas,
        odontogramas
      }
      return await buildDocMedicoDefinition(
        formContent,
        tipoImpresionAsistencialDefault.HISTORIA,
        false,
        false,
        fechaStr,
        tecnologiaNombre
      )
    }

    return {
        generarDocumentoAtencion,
        generatePdf,
        buildHistoriaDefinition,
        procesarDatosBase
    }
}
